import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BrandMark } from "@/components/BrandMark";

// Premium "Launching Soon" screen shown to the public while the storefront
// is in maintenance mode. Sits on top of the mesh gradient background.
export const MaintenanceScreen = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1000), 1200);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="maintenance-screen"
      className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 sm:px-6 py-10"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ backgroundColor: "rgba(2, 44, 34, 0.97)" }}
        className="relative w-full max-w-2xl mx-auto rounded-[32px] border border-[#E0B53C]/30 backdrop-blur-2xl shadow-[0_30px_80px_rgba(2,44,34,0.45)] overflow-hidden"
      >
        {/* Gold hairline glow at top */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E0B53C]/80 to-transparent" />

        {/* Soft ambient glows */}
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-[300px] h-[300px] rounded-full blur-[80px] opacity-30"
          style={{
            background:
              "radial-gradient(circle, #E0B53C 0%, rgba(224,181,60,0) 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-20 w-[320px] h-[320px] rounded-full blur-[90px] opacity-25"
          style={{
            background:
              "radial-gradient(circle, #064E3B 0%, rgba(6,78,59,0) 70%)",
          }}
        />

        <div className="relative px-7 sm:px-12 py-12 sm:py-16 text-center">
          {/* Brand mark + premium loader */}
          <div className="relative mx-auto w-24 h-24 sm:w-28 sm:h-28 mb-8">
            {/* Outer ring */}
            <div
              className="absolute inset-0 rounded-full border border-[#E0B53C]/25"
              aria-hidden="true"
            />
            {/* Rotating gold arc loader */}
            <svg
              className="absolute inset-0 w-full h-full animate-[spin_2.4s_linear_infinite]"
              viewBox="0 0 100 100"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="loader-gold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#E0B53C" stopOpacity="0" />
                  <stop offset="50%" stopColor="#E0B53C" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#F8E4A8" stopOpacity="1" />
                </linearGradient>
              </defs>
              <circle
                cx="50"
                cy="50"
                r="46"
                fill="none"
                stroke="url(#loader-gold)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray="80 250"
              />
            </svg>
            {/* Brand mark centre */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              >
                <BrandMark size={56} />
              </motion.div>
            </div>
          </div>

          {/* Launch date announcement banner — high-contrast, corporate */}
          <motion.div
            data-testid="launch-date-banner"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto inline-flex items-center gap-2.5 sm:gap-3 mb-7 rounded-full px-4 sm:px-6 py-2.5 sm:py-3 max-w-[92%]"
            style={{
              background:
                "linear-gradient(180deg, rgba(224,181,60,0.10) 0%, rgba(224,181,60,0.04) 100%)",
              border: "1px solid rgba(224,181,60,0.45)",
              boxShadow:
                "inset 0 1px 0 rgba(248,228,168,0.25), 0 8px 24px rgba(0,0,0,0.35)",
            }}
          >
            {/* left & right gold pips */}
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: "#E0B53C", boxShadow: "0 0 8px #E0B53C" }}
            />
            <span
              className="font-wordmark uppercase text-[11px] sm:text-base tracking-[0.16em] sm:tracking-[0.18em] leading-snug sm:leading-none text-center"
              style={{
                color: "#E0B53C",
                textShadow:
                  "0 1px 0 rgba(0,0,0,0.55), 0 2px 12px rgba(224,181,60,0.45), 0 0 1px rgba(248,228,168,0.6)",
              }}
            >
              Launching on July 10, 2026 — The Big Sale is Coming!
            </span>
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: "#E0B53C", boxShadow: "0 0 8px #E0B53C" }}
            />
          </motion.div>

          {/* Eyebrow */}
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#E0B53C] font-semibold mb-4">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E0B53C] mr-2 align-middle animate-pulse" />
            Coming soon · India
          </p>

          {/* Title */}
          <h1 className="font-wordmark text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5">
            <span style={{ color: "#E0B53C" }}>Masti</span>
            <span style={{ color: "#FFFFFF" }}>Bazaar</span>
            <span
              className="block font-display font-light italic text-2xl sm:text-3xl lg:text-4xl mt-3"
              style={{ color: "#F8E4A8" }}
            >
              is launching soon.
            </span>
          </h1>

          {/* Sub-copy */}
          <p className="text-base sm:text-lg text-white/75 max-w-md mx-auto leading-relaxed mb-8">
            We&apos;re curating an ultra-premium edit of Indian D2C drops —
            handpicked, slow-made, cash-on-delivery. The doors open soon.
          </p>

          {/* Animated progress */}
          <div
            className="mx-auto max-w-xs h-[3px] rounded-full bg-white/10 overflow-hidden"
            aria-hidden="true"
            data-testid="maintenance-loader"
          >
            <motion.div
              className="h-full rounded-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #E0B53C, #F8E4A8, #E0B53C, transparent)",
              }}
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
            />
          </div>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-white/60">
            <span>Handpicked · D2C</span>
            <span className="hidden sm:inline text-[#E0B53C]/60">◆</span>
            <span>Cash on Delivery</span>
            <span className="hidden sm:inline text-[#E0B53C]/60">◆</span>
            <span>Free Shipping ₹999+</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
